export type AtsResourceType = 'scorecard' | 'application' | 'candidate' | 'job'

interface AtsUrlConfig {
  baseUrl: string
  paths: Partial<Record<AtsResourceType, (id: string) => string>>
}

const ATS_URL_CONFIGS: Record<string, AtsUrlConfig> = {
  greenhouse: {
    baseUrl: 'https://app.greenhouse.io',
    paths: {
      scorecard: (id) => `/scorecards/${id}`,
      application: (id) => `/applications/${id}`,
      candidate: (id) => `/people/${id}`,
      job: (id) => `/plans/${id}`,
    },
  },
  lever: {
    baseUrl: 'https://hire.lever.co',
    paths: {
      candidate: (id) => `/candidates/${id}`,
      application: (id) => `/opportunities/${id}`,
      scorecard: (id) => `/interviews/${id}/feedback`,
      job: (id) => `/jobs/${id}`,
    },
  },
  ashby: {
    baseUrl: 'https://app.ashbyhq.com',
    paths: {
      candidate: (id) => `/candidates/${id}`,
      application: (id) => `/applications/${id}`,
      job: (id) => `/jobs/${id}`,
      scorecard: (id) => `/feedback/${id}`,
    },
  },
  workable: {
    baseUrl: 'https://app.workable.com',
    paths: {
      candidate: (id) => `/backend/candidates/${id}`,
      application: (id) => `/backend/candidates/${id}`,
      job: (id) => `/backend/jobs/${id}`,
      scorecard: (id) => `/backend/scorecards/${id}`,
    },
  },
  jobvite: {
    baseUrl: 'https://app.jobvite.com',
    paths: {
      candidate: (id) => `/app/#/candidates/${id}`,
      application: (id) => `/app/#/candidates/${id}`,
      job: (id) => `/app/#/jobs/${id}`,
    },
  },
  smartrecruiters: {
    baseUrl: 'https://www.smartrecruiters.com',
    paths: {
      candidate: (id) => `/app/people/${id}`,
      application: (id) => `/app/applications/${id}`,
      job: (id) => `/app/jobs/${id}`,
      scorecard: (id) => `/app/interviews/${id}`,
    },
  },
  bamboohr: {
    baseUrl: 'https://app.bamboohr.com',
    paths: {
      candidate: (id) => `/hiring/candidates/${id}`,
      application: (id) => `/hiring/candidates/${id}`,
    },
  },
}

const ATS_BRAND_COLORS: Record<
  string,
  { bg: string; hover: string; active: string }
> = {
  adp: { bg: '#D0271D', hover: '#B22119', active: '#941B15' },
  applicantstack: { bg: '#3B82F6', hover: '#3270D4', active: '#295EB2' },
  ashby: { bg: '#6558F5', hover: '#5448D4', active: '#4339B3' },
  atsanywhere: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  bamboohr: { bg: '#73C41D', hover: '#61A618', active: '#508814' },
  boondmanager: { bg: '#00A3E0', hover: '#008BBF', active: '#00739E' },
  breezyhr: { bg: '#19B5A0', hover: '#149A88', active: '#108070' },
  bullhorn: { bg: '#FF6B00', hover: '#DB5C00', active: '#B74D00' },
  careerplug: { bg: '#43A047', hover: '#388838', active: '#2E7030' },
  carerix: { bg: '#1565C0', hover: '#1156A3', active: '#0E4786' },
  cats: { bg: '#1EA8D8', hover: '#198FB8', active: '#147698' },
  checkr: { bg: '#009BB5', hover: '#00849A', active: '#006D80' },
  clearcompany: { bg: '#009688', hover: '#007E73', active: '#00675E' },
  cornerstone: { bg: '#E65100', hover: '#C44500', active: '#A23900' },
  crelate: { bg: '#344AA7', hover: '#2C3F8E', active: '#243475' },
  dayforce: { bg: '#6A1B9A', hover: '#5A1783', active: '#4A136C' },
  eightfold: { bg: '#2DB9A0', hover: '#269E89', active: '#208372' },
  eploy: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  exelare: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  factorialhr: { bg: '#FF6D4A', hover: '#DB5D40', active: '#B74E36' },
  firefish: { bg: '#FF5722', hover: '#DB4A1D', active: '#B73E18' },
  flatchr: { bg: '#3B82F6', hover: '#3270D4', active: '#295EB2' },
  fountain: { bg: '#55279C', hover: '#482185', active: '#3B1B6E' },
  gem: { bg: '#0D62FF', hover: '#0B54DB', active: '#0946B7' },
  greenhouse: { bg: '#008561', hover: '#006d4f', active: '#005a42' },
  greenhouse_ingest: { bg: '#008561', hover: '#006d4f', active: '#005a42' },
  hireology: { bg: '#CC3366', hover: '#AD2B57', active: '#8E2348' },
  homerun: { bg: '#7B68EE', hover: '#6958CB', active: '#5748A8' },
  icims: { bg: '#7C3BE2', hover: '#6932C1', active: '#5629A0' },
  indeed: { bg: '#003A9B', hover: '#003184', active: '#00286D' },
  jazzhr: { bg: '#B478D2', hover: '#9966B3', active: '#7E5494' },
  jboard: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  jobadder: { bg: '#0069B2', hover: '#005998', active: '#00497E' },
  jobdiva: { bg: '#1A73E8', hover: '#1662C6', active: '#1251A4' },
  jobscore: { bg: '#3F51B5', hover: '#36459A', active: '#2D397F' },
  jobvite: { bg: '#E7553D', hover: '#CF4A35', active: '#B7402E' },
  jungle: { bg: '#43A047', hover: '#388838', active: '#2E7030' },
  lever: { bg: '#6B47DC', hover: '#5A3BBF', active: '#4A30A2' },
  loxo: { bg: '#005BF7', hover: '#004DD3', active: '#003FAF' },
  manatal: { bg: '#4361EE', hover: '#3952CB', active: '#2F43A8' },
  mercuryats: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  oracletaleo: { bg: '#C74634', hover: '#A93B2C', active: '#8B3024' },
  paradox: { bg: '#1EB5BC', hover: '#199A9F', active: '#148082' },
  paycor: { bg: '#F37321', hover: '#CF621C', active: '#AB5117' },
  paylocityrecruiting: { bg: '#003DA5', hover: '#00348C', active: '#002B73' },
  pcrecruiter: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  pcrecruiteronprem: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  personio: { bg: '#00A47E', hover: '#008B6B', active: '#007258' },
  phenom: { bg: '#6200EA', hover: '#5300C7', active: '#4400A4' },
  pinpoint: { bg: '#9B51E0', hover: '#8344BF', active: '#6B389E' },
  plum: { bg: '#9C27B0', hover: '#842196', active: '#6C1B7C' },
  prismhr: { bg: '#1565C0', hover: '#1156A3', active: '#0E4786' },
  recooty: { bg: '#42A5F5', hover: '#388CD1', active: '#2E73AD' },
  recruitcrm: { bg: '#2962FF', hover: '#2353DB', active: '#1D44B7' },
  recruitee: { bg: '#6C63FF', hover: '#5B53DB', active: '#4A44B7' },
  recruiterflow: { bg: '#2196F3', hover: '#1C7FCF', active: '#1768AB' },
  recruitwizard: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  sagehr: { bg: '#00D639', hover: '#00B530', active: '#009428' },
  seven20: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  smartrecruiters: { bg: '#0B69FF', hover: '#0957DB', active: '#0746B7' },
  stardex: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  successfactors: { bg: '#0070F2', hover: '#005FCF', active: '#004EAC' },
  successfactors_oidc: { bg: '#0070F2', hover: '#005FCF', active: '#004EAC' },
  talentlyft: { bg: '#2979FF', hover: '#2367DB', active: '#1D55B7' },
  teamtailor: { bg: '#F43F85', hover: '#D03670', active: '#AD2D5E' },
  trackerrms: { bg: '#1976D2', hover: '#1564B3', active: '#115294' },
  trakstarhire: { bg: '#FF6F00', hover: '#DB5F00', active: '#B74F00' },
  tribepad: { bg: '#00A75D', hover: '#008D4F', active: '#007341' },
  ukgpro_ats: { bg: '#00875A', hover: '#00724C', active: '#005D3E' },
  vincere: { bg: '#E4173F', hover: '#C21436', active: '#A0112D' },
  workable: { bg: '#1999E3', hover: '#1480C2', active: '#1068A1' },
  workday: { bg: '#0057AE', hover: '#004A94', active: '#003D7A' },
  workday_legacy: { bg: '#0057AE', hover: '#004A94', active: '#003D7A' },
  workstream: { bg: '#1B66FF', hover: '#1757DB', active: '#1348B7' },
  zohorecruit: { bg: '#DC4A38', hover: '#BB3F30', active: '#9A3428' },
}

const DEFAULT_BRAND_COLORS = ATS_BRAND_COLORS.greenhouse

export function getAtsBrandColors(integrationType: string | null | undefined): {
  bg: string
  hover: string
  active: string
} {
  const key = integrationType?.toLowerCase()
  return (key && ATS_BRAND_COLORS[key]) || DEFAULT_BRAND_COLORS
}

export function buildAtsUrl({
  integrationType,
  resourceType,
  resourceId,
}: {
  integrationType: string | null | undefined
  resourceType: AtsResourceType
  resourceId: string
}): string | null {
  if (!integrationType || !resourceId) {
    return null
  }

  const normalizedType = integrationType.toLowerCase()
  const config = ATS_URL_CONFIGS[normalizedType]

  if (!config) {
    return null
  }

  const pathBuilder = config.paths[resourceType]
  if (!pathBuilder) {
    return null
  }

  return `${config.baseUrl}${pathBuilder(resourceId)}`
}

export function getSupportedResourceTypes(
  integrationType: string | null | undefined,
): AtsResourceType[] {
  if (!integrationType) {
    return []
  }

  const normalizedType = integrationType.toLowerCase()
  const config = ATS_URL_CONFIGS[normalizedType]

  if (!config) {
    return []
  }

  return Object.keys(config.paths) as AtsResourceType[]
}
